import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';
import { DEFAULT_COMPARISON_OPERATOR } from '../../constants/comparisonOperators';

// Import getCvTemplate from cached implementation
import { getCvTemplate } from './cached/cvApi';

/**
 * API services for CV-related operations
 */
export const cvApi = {
  // Use the cached implementation
  getCvTemplate,
  
  /**
   * Get all CVs from the backend with cursor-based pagination
   * @param {Object} options - Pagination options
   * @param {number} options.limit - Number of items to return (default: 10)
   * @param {string} options.cursor - The cursor value to fetch the next page
   * @param {string} options.orderBy - Field to sort by (default: "fullName")
   * @param {string} options.orderDirection - Sort direction, "asc" or "desc" (default: "asc")
   * @param {string} options.search - Text to filter CVs by fullName, case insensitive
   * @param {string[]} options.positionIds - Array of position IDs to filter CVs by
   * @param {boolean|null} options.linkedFilter - Filter by linked status: true = linked only, false = unlinked only, null = all
   * @param {string|null} options.languageFilter - Language ID to filter CVs by
   * @param {string|null} options.levelFilter - Level ID to filter CVs by
   * @param {string} options.levelComparator - Level comparison operator: "gte", "eq", "lte" (default: "gte")
   * @param {string[]|null} options.skillsFilter - Array of skill IDs to filter CVs by
   * @returns {Promise<Object>} - Object containing items array and pageInfo
   */
  getAllCVs: async (options = {}) => {
    try {
      const { 
        limit = 10, 
        cursor, 
        orderBy = 'fullName', 
        orderDirection = 'asc', 
        search, 
        positionIds, 
        linkedFilter,
        languageFilter,
        levelFilter,
        levelComparator = DEFAULT_COMPARISON_OPERATOR,
        skillsFilter
      } = options;
      
      let url = `${API_BASE_URL}/cvs?limit=${limit}`;
      if (cursor) url += `&cursor=${cursor}`;
      if (orderBy) url += `&orderBy=${orderBy}`;
      if (orderDirection) url += `&orderDirection=${orderDirection}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (positionIds && positionIds.length > 0) {
        // Join array values with comma for server-side parsing
        url += `&positionIds=${positionIds.join(',')}`;
      }
      if (linkedFilter !== null && linkedFilter !== undefined) {
        url += `&linked=${linkedFilter}`;
      }
      if (languageFilter) {
        url += `&languageId=${languageFilter}`;
      }
      if (levelFilter) {
        url += `&levelId=${levelFilter}`;
        url += `&levelComparator=${levelComparator}`;
      }
      if (skillsFilter && skillsFilter.length > 0) {
        // Join array values with comma for server-side parsing
        url += `&skillIds=${skillsFilter.join(',')}`;
      }
      
      return await authenticatedApi.get(url);
    } catch (error) {
      console.error('Error fetching CVs');
      throw new ApiError('Failed to fetch CVs', error.status || 500);
    }
  },
  
  /**
   * Get CVs by employee ID from the backend with cursor-based pagination
   * @param {string} employeeId - The ID of the employee
   * @param {Object} options - Pagination options
   * @param {number} options.limit - Number of items to return (default: 10)
   * @param {string} options.cursor - The cursor value to fetch the next page
   * @param {string} options.orderBy - Field to sort by (default: "updatedAt")
   * @param {string} options.orderDirection - Sort direction, "asc" or "desc" (default: "desc")
   * @param {string} options.search - Text to filter CVs by fullName, case insensitive
   * @param {string[]} options.positionIds - Array of position IDs to filter CVs by
   * @returns {Promise<Object>} - Object containing items array and pageInfo
   */
  getCVsByEmployeeId: async (employeeId, options = {}) => {
    try {
      const { limit = 10, cursor, orderBy = 'updatedAt', orderDirection = 'desc', search, positionIds } = options;
      
      let url = `${API_BASE_URL}/employee/${employeeId}/cvs?limit=${limit}`;
      if (cursor) url += `&cursor=${cursor}`;
      if (orderBy) url += `&orderBy=${orderBy}`;
      if (orderDirection) url += `&orderDirection=${orderDirection}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (positionIds && positionIds.length > 0) {
        // Join array values with comma for server-side parsing
        url += `&positionIds=${positionIds.join(',')}`;
      }
      
      return await authenticatedApi.get(url);
    } catch (error) {
      console.error('Error fetching employee CVs');
      throw new ApiError('Failed to fetch employee CVs', error.status || 500);
    }
  },
  
  /**
   * Get CV data by ID from the backend
   * @param {string} id - The ID of the CV to retrieve
   * @returns {Promise<Object>} - The CV data
   */
  getCVById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/${id}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to view this CV', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'CV not found', 404);
      }
      console.error('Error fetching CV data');
      throw new ApiError('Failed to fetch CV data', error.status || 500);
    }
  },
  
  /**
   * Save CV data to the backend
   * @param {Object} cvData - The CV data to save
   * @returns {Promise<Object>} - The saved CV data
   */
  saveCV: async (cvData) => {
    try {
      // If there's an ID, update existing CV, otherwise create new
      //if (cvData.id) {
      //  return await authenticatedApi.put(`${API_BASE_URL}/cv/${cvData.id}`, cvData);
      //} else {
        return await authenticatedApi.post(`${API_BASE_URL}/cv`, cvData);
      //}
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid CV data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to modify this CV', 403);
      }
      console.error('Error saving CV data');
      throw new ApiError('Failed to save CV data', error.status || 500);
    }
  },

  /**
   * Delete CV by ID from the backend
   * @param {string} id - The ID of the CV to delete
   * @returns {Promise<Object>} - The delete response
   */
  deleteCV: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/cv/${id}`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to delete this CV', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'CV not found', 404);
      }
      console.error('Error deleting CV');
      throw new ApiError('Failed to delete CV', error.status || 500);
    }
  },

  /**
   * Check if the current user can access (manage) a CV for a specific employee
   * @param {string} employeeId - The ID of the employee whose CV access should be checked
   * @returns {Promise<Object>} - Object containing canAccess boolean and employeeId
   */
  canAccessCV: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/cv/can-access/${employeeId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Employee ID is required', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You don\'t have permission to access this resource', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Employee not found', 404);
      }
      console.error('Error checking CV access');
      throw new ApiError('Failed to check CV access', error.status || 500);
    }
  },

  // Add other CV-related API endpoints here
};

export default cvApi; 